import { useEffect, useState } from 'react';
import { authClient } from '../lib/auth-client';
import {
  clearHadSession,
  hadSession,
  markHadSession,
  reportClientDiag,
} from '../lib/sessionDiagnostics';

const RETRY_DELAY_MS = 400;
const RETRY_GRACE_MS = 2500;

type RecoveryState = 'none' | 'active' | 'done';

// Wraps authClient.useSession with a one-shot recovery retry: when the session
// resolves null on a browser that previously held a session (localStorage
// hint), the null is treated as suspect — the pending state is held and the
// session is refetched once before "logged out" is accepted. This stops a
// single transient get-session failure on cold load (the iOS login race) from
// dropping a logged-in user on the login page. Genuinely logged-out users pay
// the grace delay at most once: a confirmed null clears the hint.
//
// Recovery is a one-way state machine (none -> active -> done) so the attempt
// runs exactly once per mount. isPending oscillates during the refetch and
// must NOT gate the recovery effect — deriving the retry condition from it
// cancels the grace timer mid-flight and restarts the attempt in a loop.
export const useResilientSession = () => {
  const { data: session, isPending, error, refetch } = authClient.useSession();
  const [recovery, setRecovery] = useState<RecoveryState>('none');

  // Latch into recovery on the first settled null observed with the hint set.
  useEffect(() => {
    if (recovery === 'none' && !isPending && !session && hadSession()) {
      setRecovery('active');
    }
  }, [recovery, isPending, session]);

  // Run the recovery attempt while active. Keyed on `recovery` only.
  useEffect(() => {
    if (recovery !== 'active') {
      return;
    }
    reportClientDiag('session-null-retrying', {});
    const retryTimer = setTimeout(() => refetch(), RETRY_DELAY_MS);
    const graceTimer = setTimeout(() => setRecovery('done'), RETRY_GRACE_MS);

    return () => {
      clearTimeout(retryTimer);
      clearTimeout(graceTimer);
    };
  }, [recovery, refetch]);

  // Session present: remember the hint; if a recovery was in flight, it worked.
  useEffect(() => {
    if (!session?.user) {
      return;
    }
    markHadSession();

    if (recovery === 'active') {
      setRecovery('done');
      reportClientDiag('session-recovered-after-retry', {});
    }
  }, [session?.user, recovery]);

  // Recovery ended with no session: accept logged-out and clear the hint so
  // subsequent loads show the login page without the grace delay.
  useEffect(() => {
    if (recovery === 'done' && !session) {
      clearHadSession();
      reportClientDiag('session-null-confirmed', {});
    }
  }, [recovery, session]);

  // Hold the pending state while recovering — including the render frames
  // between the settled null arriving and the latch effect running.
  const holdingForRecovery =
    recovery === 'active' ||
    (recovery === 'none' && !isPending && !session && hadSession());

  return { data: session, isPending: isPending || holdingForRecovery, error };
};
